import React, { useEffect, useState } from 'react';
import { ReservationReturn } from '../clients/rust-api-client';
import { memberReservationService } from '../clients/ApiClients';
import { Button } from '../Components/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../Components/Catalyst/dialog';
import { Field, Label } from '../Components/Catalyst/fieldset';
import { Textarea } from '../Components/Catalyst/textarea';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import {
  cancelOutcome,
  formatLocalRange,
  nowLocalIn,
  outcomeCosts,
} from './reservationFormat';
import { reservationErrorMessage } from './ruleMessages';

interface Props {
  reservation: ReservationReturn | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updated: ReservationReturn) => void;
}

/**
 * Cancelling a booking, or withdrawing a request. Says what happens to
 * the fee *before* the homeowner confirms — reversed before the deadline;
 * after it kept, or swapped for a late-cancellation charge (#640 phase 2)
 * — in the server's words (`cancel_outcome_now`) when it gives them.
 */
const CancelReservationDialog: React.FC<Props> = ({
  reservation,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError(null);
    }
  }, [isOpen]);

  if (!reservation) return null;

  const pending = reservation.status === 'pending';
  const outcome = cancelOutcome(reservation, nowLocalIn(reservation.timezone));

  const confirm = async (): Promise<void> => {
    setWorking(true);
    setError(null);
    try {
      const { data } = await memberReservationService.cancelMyReservation({
        reservationId: reservation.id,
        memberReservationCancel: { reason: reason.trim() || null },
      });
      onSuccess(data);
    } catch (err) {
      setError(
        reservationErrorMessage(
          err,
          pending ? "Couldn't withdraw this request." : "Couldn't cancel this booking."
        )
      );
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} size="md">
      <DialogTitle>{pending ? 'Withdraw this request?' : 'Cancel this booking?'}</DialogTitle>
      <DialogDescription>
        {reservation.amenity_name} ·{' '}
        {formatLocalRange(reservation.start_local, reservation.end_local)}
      </DialogDescription>
      <DialogBody className="space-y-4">
        <p
          className={
            outcomeCosts(outcome.kind)
              ? 'rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200'
              : 'text-sm text-zinc-700 dark:text-zinc-300'
          }
        >
          {outcome.message}
        </p>
        <Field>
          <Label>Reason (optional)</Label>
          <Textarea
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        <ErrorAlert>{error}</ErrorAlert>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={working}>
          Keep it
        </Button>
        <Button color="red" onClick={confirm} disabled={working}>
          {working ? 'Working…' : pending ? 'Withdraw request' : 'Cancel booking'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelReservationDialog;
